import { type AnyAction, combineReducers } from 'redux';

import type { TabDurationStore } from 'js/src/data/analytics';
import {
    initializeTabDurations,
    syncDurationForTab,
} from 'js/src/data/analytics/analytics-actions';
import { EDITOR_TAB_OPTIONS } from 'js/src/data/app/app-types';
import TimeTracker from 'js/src/libs/TimeTracker';

const time_tracker = new TimeTracker(0);

const INITIAL_TAB_DURATIONS: TabDurationStore = {
    media_tab_duration: 0,
    music_tab_duration: 0,
    style_tab_duration: 0,
    format_tab_duration: 0,
    voiceover_tab_duration: 0,
    original_content_tab_duration: 0,
};

const DURATION_KEY_BY_TAB: Partial<Record<string, keyof TabDurationStore>> = {
    [EDITOR_TAB_OPTIONS.MEDIA]: 'media_tab_duration',
    [EDITOR_TAB_OPTIONS.MUSIC]: 'music_tab_duration',
    [EDITOR_TAB_OPTIONS.STYLE]: 'style_tab_duration',
    [EDITOR_TAB_OPTIONS.FORMAT]: 'format_tab_duration',
    [EDITOR_TAB_OPTIONS.VOICEOVER]: 'voiceover_tab_duration',
    [EDITOR_TAB_OPTIONS.ORIGINAL_CONTENT]: 'original_content_tab_duration',
};

export function tab_durations(
    state: TabDurationStore = INITIAL_TAB_DURATIONS,
    action: AnyAction,
): TabDurationStore {
    if (initializeTabDurations.match(action)) {
        time_tracker.clear();
        const initialized: TabDurationStore = {
            media_tab_duration: action.payload.media ?? 0,
            music_tab_duration: action.payload.music ?? 0,
            style_tab_duration: action.payload.style ?? 0,
            format_tab_duration: action.payload.format ?? 0,
            voiceover_tab_duration: action.payload.voiceover ?? 0,
            original_content_tab_duration: action.payload.original_content ?? 0,
        };
        const isUnchanged = (Object.keys(initialized) as (keyof TabDurationStore)[]).every(
            (key) => initialized[key] === state[key],
        );
        return isUnchanged ? state : initialized;
    }
    // update the stored time on explicit request (syncDurationForTab) or when switching tabs (switchEditorTab)
    if (syncDurationForTab.match(action)) {
        // get the time that's passed & reset the tracker
        const secondsPassed = time_tracker.getSecondsPassed();
        time_tracker.clear();

        const durationKey = DURATION_KEY_BY_TAB[action.payload.currentTab];
        if (!durationKey || secondsPassed === 0) {
            return state;
        }
        return { ...state, [durationKey]: state[durationKey] + secondsPassed };
    }
    return state;
}

// now we combine the reducers into their final shape
export const analytics = combineReducers({
    tab_durations,
});

export type AnalyticsState = ReturnType<typeof analytics>;
